'use strict';

// Vue
import Vue from 'vue';
import router from 'router';

// 3rd party
import moment from 'moment';

// API
import {
  requestProviderList,
  requestProviderSchedule,
} from 'api/provider';

import {
  requestFakeUserToken,
} from 'api/auth';

// Models
import states from 'models/contact/states';

// State
const state = {
  availabilities: [
    [], [], [], [], [], [], [],
  ],
  currentStartDate: moment().format('YYYY-mm-DD'),
  noResults: false,
  providers: [],
};

// Getters
const getters = {
  loadingListOfProviders: state => state.loadingListOfProviders,
  loadingProviderDetails: state => state.loadingProviderDetails,
  loadingAvailabilities: state => state.loadingAvailabilities,
  providers: state => state.providers,
  provider: state => state.provider,
  availabilityTitles: state => state.availabilityTitles,
  providerDetails: state => state.providerDetails,
  availabilities: state => state.availabilities,
  selectedSlot: state => state.selectedSlot,
  selectedAppointmentType: state => state.selectedAppointmentType,
  noResults: state => state.noResults,
};

// Mutations
const mutations = {
  TOGGLE_LOADING (state, payload) {
    Vue.set(state, payload.key, payload.value);
  },

  HAS_RESULTS(state, payload) {
    state.noResults = payload;
  },

  SAVE_FAKE_USER_DATA ( state, payload ) {
    Vue.set(state, 'fakeUser', payload);
  },

  SAVE_LIST_OF_PROVIDERS ( state, payload ) {
    Vue.set(state, 'providers', payload);
  },

  SAVE_SELECTED_PROVIDER_TO_STATE ( state, provider ) {
    Vue.set(state, 'provider', provider);
  },

  SAVE_PROVIDER_DETAILS ( state, payload ) {
    Vue.set(state, 'providerDetails', payload);
  },

  SAVE_AVAILABILITY_TITLES ( state, payload ) {
    Vue.set(state, 'availabilityTitles', payload);
  },

  SAVE_PROVIDER_AVAILABILITY ( state, payload ) {
    // Empty availabilties first
    state.availabilities = [];

    _.forEach(payload, day => { // eslint-disable-line no-undef
      state.availabilities.push(day);
    });
  },

  SAVE_SELECTED_SLOT ( state, slot ) {
    Vue.set(state, 'selectedSlot', slot);
  },

  SAVE_SELECTED_APPOINTMENT_TYPE ( state, type ) {
    Vue.set(state, 'selectedAppointmentType', type);
  },
};

// Actions
const actions = {
  async getListOfProviders({ commit, rootGetters, dispatch }, model) {
    if (
      !localStorage.getItem('answer_token') &&
      !localStorage.getItem('mdLiveAuthToken')
    ) {
      // TODO: Check if user is logged in, if so, redirect to homepage else to supports
      router.push({
        name: 'support',
      });
    }

    if (!localStorage.getItem('mdLiveAuthToken')) {
      const fakeUserResponse = await requestFakeUserToken({
        header: {
          headers: {
            Authorization: `Token ${localStorage.getItem('answer_token')}`,
          },
        },
      });
  
      localStorage.setItem('fake_user_token', fakeUserResponse.data.jwt);
      localStorage.setItem('fake_user_id', fakeUserResponse.data.user.id);
    }

    commit('TOGGLE_LOADING', {
      key: 'loadingListOfProviders',
      value: true,
    });

    let user;
    if ( localStorage.getItem('updateUser') ) {
      user = JSON.parse(localStorage.getItem('updateUser'));
    }

    if (
      localStorage.getItem('answer_token') &&
      rootGetters['auth/activeHeader'] !== 'answer' ) {

      commit('auth/SET_HEADERS', 'answer', { root: true });
    }

    if (
      localStorage.getItem('mdLiveAuthToken') &&
      rootGetters['auth/activeHeader'] !== 'mdLive' ) {

      commit('auth/SET_HEADERS', 'mdLive', { root: true });
    }

    let stateAbbrev;

    if (!JSON.parse(localStorage.getItem('updateUser'))) {
      stateAbbrev = _.find(states, state => state.label === localStorage.getItem('answerLocation')).value; // eslint-disable-line no-undef
    } else {
      stateAbbrev = JSON.parse(localStorage.getItem('updateUser')).appointment_state;
    }

    const data = {
      body: {
        search: {
          state_abbrev: model.userState || stateAbbrev,
          provider_type_id: rootGetters['app/org'].mdlive_provider_type,
        },
      },
      id: user ? user.md_live.user.id : 'default',
      page: model.page,
      token: localStorage.getItem('fake_user_token'),
      patient_id: localStorage.getItem('fake_user_id'),
    };

    const response = await requestProviderList(data);

    if ( response.status === 200 ) {
      if (model.userState && model.page === 1) {
        commit('HAS_RESULTS', false);
        commit('SAVE_LIST_OF_PROVIDERS', []);
      }
      if (!state.noResults && response.data.providers.length > 0) {
        commit('SAVE_LIST_OF_PROVIDERS', state.providers.concat(response.data.providers));
        commit('HAS_RESULTS', false);
      }

      if (model.page === 1 && !response.data.providers.length ) {
        dispatch('app/setErrorMessage', 'There are currently no providers<br class="hide-below-medium"> available at your location', { root: true });

        router.push({
          name: 'error',
        });
      }

      if (model.page > 1 && !response.data.providers.length > 0) {
        commit('HAS_RESULTS', true);
      }
    }

    commit('TOGGLE_LOADING', {
      key: 'loadingListOfProviders',
      value: false,
    });
  },

  saveSelectedProvider({ commit }, provider) {
    commit('SAVE_SELECTED_PROVIDER_TO_STATE', provider);
  },

  async getProviderDetail({ commit, dispatch, state, rootGetters }, date) {
    // if ( !state.provider ) {
    //   router.push({
    //     name: `support`,
    //   });
    //
    //   return;
    // }

    commit('TOGGLE_LOADING', {
      key: 'loadingProviderDetails',
      value: true,
    });

    if (
      localStorage.getItem('answer_token') &&
      rootGetters['auth/activeHeader'] !== 'answer' ) {

      commit('auth/SET_HEADERS', 'answer', { root: true });
    }

    if (
      localStorage.getItem('mdLiveAuthToken') &&
      rootGetters['auth/activeHeader'] !== 'mdLive' ) {

      commit('auth/SET_HEADERS', 'mdLive', { root: true });
    }

    // simultaneously get availabilities
    dispatch('getProviderAvailability', date);

    // Request endpoint for provider details (not using the schedule data here)
    let user;
    if ( localStorage.getItem('updateUser') ) {
      user = JSON.parse(localStorage.getItem('updateUser'));
    }

    const model = {
      id: state.provider.id,
      config: {
        params: {
          specific_date: moment(date).format('YYYY-MM-DD'),
          show_next_availability: false,
        },
      },
      url: user ? 'mdlife' : 'default',
      patient_id: localStorage.getItem('answer_id'),
      token: localStorage.getItem('fake_user_token'),
    };

    const response = await requestProviderSchedule(model);

    const providerDetails = {
      ...response.data.provider_details,
      patient_appointment_types: response.data.availability_details.patient_appointment_types,
    };

    commit('SAVE_PROVIDER_DETAILS', providerDetails);

    commit('TOGGLE_LOADING', {
      key: 'loadingProviderDetails',
      value: false,
    });

    return providerDetails;
  },

  getNextAvailability({ state }) {
    let user;

    if ( localStorage.getItem('updateUser') ) {
      user = JSON.parse(localStorage.getItem('updateUser'));
    }

    const model = {
      id: state.provider.id,
      config: {
        params: {
          specific_date: moment().format('YYYY-MM-DD'),
          show_next_availability: true,
        },
      },
      url: user ? 'mdlife' : 'default',
      token: localStorage.getItem('fake_user_token'),
    };

    const response = requestProviderSchedule(model);
    
    return response;
  
  },

  getProviderAvailability({ commit, state, rootGetters }, date) {
    commit('TOGGLE_LOADING', {
      key: 'loadingAvailabilities',
      value: true,
    });

    if (
      localStorage.getItem('answer_token') &&
      rootGetters['auth/activeHeader'] !== 'answer' ) {

      commit('auth/SET_HEADERS', 'answer', { root: true });
    }

    if (
      localStorage.getItem('mdLiveAuthToken') &&
      rootGetters['auth/activeHeader'] !== 'mdLive' ) {

      commit('auth/SET_HEADERS', 'mdLive', { root: true });
    }

    const datesToRequest = [];
    const selectedDate = moment(date);
    const selectedDay = selectedDate.day() || 7;
    const firstDayOfThisWeek = selectedDate.subtract(selectedDay - 1, 'd').format();


    for (let i = 0; i < 7; i++) {
      datesToRequest.push(moment(firstDayOfThisWeek).add(i, 'd').format());
    }

    // Saving the dates that are being used as title in the schedule
    commit('SAVE_AVAILABILITY_TITLES', datesToRequest);

    const promises = [];
    const days = {};
    let user;

    if ( localStorage.getItem('updateUser') ) {
      user = JSON.parse(localStorage.getItem('updateUser'));
    }

    _.forEach(datesToRequest, date => { // eslint-disable-line no-undef
      const model = {
        id: state.provider.id,
        config: {
          params: {
            specific_date: moment(date).format('YYYY-MM-DD'),
            show_next_availability: false,
          },
        },
        url: user ? 'mdlife' : 'default',
        token: localStorage.getItem('fake_user_token'),
      };

      promises.push(requestProviderSchedule(model));
    });

    Promise.all(promises).then((responses) => {
      _.forEach(responses, response => { // eslint-disable-line no-undef
        const day = response.data.availability_details;
        const slot = moment(day.appointment_date).format('YYYY-MM-DD');

        if ( !days[slot] ) { days[slot] = []; }
        days[slot].push(...day.available_hours);
      });

      const schedule = {};
      const clonedDays = _.clone(days); // eslint-disable-line no-undef
      _.forEach(clonedDays, (day, key) => { // eslint-disable-line no-undef
        if ( !schedule[key] ) { schedule[key] = []; }


        const group = _.groupBy(day, slot => moment(slot.timeslot).format('YYYY-MM-DD')); // eslint-disable-line no-undef

        _.forEach(group, (value, key) => { // eslint-disable-line no-undef
          if ( !schedule[key] ) { schedule[key] = []; }
          schedule[key].push(...value);
        });
      });

      // Delete the saturday if exists, we're not showing weekends
      if ( Object.keys(schedule).length > 7 ) { // eslint-disable-line no-undef
        delete schedule[Object.keys(schedule)[Object.keys(schedule).length - 1]];
      }

      commit('SAVE_PROVIDER_AVAILABILITY', schedule);

      commit('TOGGLE_LOADING', {
        key: 'loadingAvailabilities',
        value: false,
      });
    });
  },

  selectSlot({ commit }, slot) {
    commit('SAVE_SELECTED_SLOT', slot);
  },

  selectAppointmentType({ commit }, type ) {
    commit('SAVE_SELECTED_APPOINTMENT_TYPE', type);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};



// WEBPACK FOOTER //
// ./client/store/provider.js